import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import PageNavbar from "components/PageNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";
import Card from "@mui/material/Card";
import { Grid, MenuItem, Select, FormControl, CircularProgress, Snackbar, Alert } from "@mui/material";
import { IoBusinessOutline, IoPerson, IoCheckmarkCircle } from "react-icons/io5";
import axios from "axios";
import { API_URL } from "../../config/api";

function UserSubmissionForm() {
  const [userType, setUserType] = useState("");
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form field configurations for each user type
  const formFields = {
    supplier: [
      { name: "companyName", label: "Company Name", type: "text", required: true },
      { name: "contactPerson", label: "Contact Person", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      { name: "businessRegistrationNumber", label: "Business Registration Number", type: "text", required: true },
      { name: "productsServices", label: "Products/Services Offered", type: "textarea", required: true },
      { name: "yearsInBusiness", label: "Years in Business", type: "number", required: true },
      { name: "address", label: "Address", type: "textarea", required: true },
    ],
    contractor: [
      { name: "companyName", label: "Company Name", type: "text", required: true },
      { name: "contactPerson", label: "Contact Person", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      { name: "licenseNumber", label: "License Number", type: "text", required: true },
      { name: "specialization", label: "Specialization", type: "select", required: true, options: ["Electrical", "Plumbing", "HVAC", "Painting", "Carpentry", "General Construction"] },
      { name: "yearsOfExperience", label: "Years of Experience", type: "number", required: true },
      { name: "portfolioWebsite", label: "Portfolio/Website URL", type: "url", required: false },
      { name: "address", label: "Address", type: "textarea", required: true },
    ],
    constructor: [
      { name: "companyName", label: "Company Name", type: "text", required: true },
      { name: "contactPerson", label: "Contact Person", type: "text", required: true },
      { name: "email", label: "Email Address", type: "email", required: true },
      { name: "phone", label: "Phone Number", type: "tel", required: true },
      { name: "constructionLicenseNumber", label: "Construction License Number", type: "text", required: true },
      { name: "projectTypes", label: "Project Types", type: "select", required: true, options: ["Residential", "Commercial", "Industrial", "Mixed-Use"] },
      { name: "companySize", label: "Company Size", type: "select", required: true, options: ["Small (1-50 employees)", "Medium (51-200 employees)", "Large (200+ employees)"] },
      { name: "certifications", label: "Certifications", type: "textarea", required: true },
      { name: "address", label: "Address", type: "textarea", required: true },
    ],
  };

  const handleUserTypeChange = (event) => {
    setUserType(event.target.value);
    setFormData({}); // Reset form data when user type changes
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate all required fields
      const fields = formFields[userType];
      const missingFields = fields
        .filter(field => field.required && !formData[field.name])
        .map(field => field.label);

      if (missingFields.length > 0) {
        setSnackbar({
          open: true,
          message: `Please fill in: ${missingFields.join(", ")}`,
          severity: "error"
        });
        setLoading(false);
        return;
      }

      // Submit to API
      const response = await axios.post(`${API_URL}/submissions`, {
        userType,
        data: formData
      });

      if (response.data.success) {
        setSnackbar({
          open: true,
          message: "Form submitted successfully!",
          severity: "success"
        });
        // Reset form
        setFormData({});
        setUserType("");
      }
    } catch (error) {
      console.error("Submission error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error submitting form. Please try again.",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (field) => {
    if (field.type === "select") {
      return (
        <FormControl fullWidth key={field.name}>
          <VuiTypography variant="caption" color="white" fontWeight="bold" mb="5px">
            {field.label} {field.required && <span style={{ color: "#e91e63" }}>*</span>}
          </VuiTypography>
          <Select
            name={field.name}
            value={formData[field.name] || ""}
            onChange={handleInputChange}
            required={field.required}
            sx={{
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "12px",
              color: "white",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              "& .MuiSelect-select": {
                padding: "12px",
                color: "white"
              },
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none"
              }
            }}
          >
            <MenuItem value="">Select {field.label}</MenuItem>
            {field.options.map(option => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === "textarea") {
      return (
        <VuiBox key={field.name}>
          <VuiTypography variant="caption" color="white" fontWeight="bold" mb="5px">
            {field.label} {field.required && <span style={{ color: "#e91e63" }}>*</span>}
          </VuiTypography>
          <VuiInput
            name={field.name}
            value={formData[field.name] || ""}
            onChange={handleInputChange}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            multiline
            rows={3}
            required={field.required}
            sx={{
              background: "rgba(0, 0, 0, 0.3)",
              borderRadius: "12px"
            }}
          />
        </VuiBox>
      );
    }

    return (
      <VuiBox key={field.name}>
        <VuiTypography variant="caption" color="white" fontWeight="bold" mb="5px">
          {field.label} {field.required && <span style={{ color: "#e91e63" }}>*</span>}
        </VuiTypography>
        <VuiInput
          type={field.type}
          name={field.name}
          value={formData[field.name] || ""}
          onChange={handleInputChange}
          placeholder={`Enter ${field.label.toLowerCase()}`}
          required={field.required}
          sx={{
            background: "rgba(0, 0, 0, 0.3)",
            borderRadius: "12px"
          }}
        />
      </VuiBox>
    );
  };

  return (
    <DashboardLayout>
      <PageNavbar />
      <VuiBox py={3}>
        <VuiBox mb={3}>
          <Grid container justifyContent="center">
            <Grid item xs={12} lg={10}>
              <Card
                sx={{
                  background: "linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)",
                  backdropFilter: "blur(120px)",
                  border: "2px solid rgba(255, 255, 255, 0.125)",
                  borderRadius: "20px",
                  padding: "30px",
                }}
              >
                {/* Header */}
                <VuiBox display="flex" alignItems="center" mb={4}>
                  <VuiBox
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      background: "linear-gradient(97.89deg, #0075ff, #0075ff)",
                      width: "60px",
                      height: "60px",
                      borderRadius: "12px",
                      marginRight: "20px",
                    }}
                  >
                    <IoBusinessOutline size="30px" color="white" />
                  </VuiBox>
                  <VuiBox>
                    <VuiTypography variant="h3" color="white" fontWeight="bold">
                      User Registration Form
                    </VuiTypography>
                    <VuiTypography variant="button" color="text" fontWeight="regular">
                      Register as a Supplier, Contractor, or Constructor
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                  {/* User Type Selection */}
                  <VuiBox mb={3}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold" mb="5px">
                      Select User Type <span style={{ color: "#e91e63" }}>*</span>
                    </VuiTypography>
                    <FormControl fullWidth>
                      <Select
                        value={userType}
                        onChange={handleUserTypeChange}
                        required
                        displayEmpty
                        sx={{
                          background: "rgba(67, 24, 255, 0.15)",
                          borderRadius: "12px",
                          color: "white",
                          border: "2px solid rgba(67, 24, 255, 0.5)",
                          fontSize: "1rem",
                          fontWeight: "bold",
                          "& .MuiSelect-select": {
                            padding: "15px",
                            color: "white"
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none"
                          },
                          "&:hover": {
                            background: "rgba(67, 24, 255, 0.25)"
                          }
                        }}
                      >
                        <MenuItem value="" disabled>Choose your category</MenuItem>
                        <MenuItem value="supplier">Supplier</MenuItem>
                        <MenuItem value="contractor">Contractor</MenuItem>
                        <MenuItem value="constructor">Constructor</MenuItem>
                      </Select>
                    </FormControl>
                  </VuiBox>

                  {/* Dynamic Form Fields */}
                  {userType && (
                    <VuiBox
                      sx={{
                        background: "rgba(255, 255, 255, 0.02)",
                        borderRadius: "15px",
                        padding: "25px",
                        border: "1px solid rgba(255, 255, 255, 0.1)"
                      }}
                    >
                      <VuiBox display="flex" alignItems="center" mb={3}>
                        <IoPerson size="24px" color="#0075ff" style={{ marginRight: "10px" }} />
                        <VuiTypography variant="h5" color="white" fontWeight="bold">
                          {userType.charAt(0).toUpperCase() + userType.slice(1)} Information
                        </VuiTypography>
                      </VuiBox>

                      <Grid container spacing={3}>
                        {formFields[userType].map((field) => (
                          <Grid item xs={12} md={6} key={field.name}>
                            {renderFormField(field)}
                          </Grid>
                        ))}
                      </Grid>

                      {/* Submit Button */}
                      <VuiBox mt={4} display="flex" justifyContent="center">
                        <VuiButton
                          type="submit"
                          color="info"
                          disabled={loading}
                          sx={{
                            background: "linear-gradient(97.89deg, #0075ff, #0075ff)",
                            padding: "12px 40px",
                            fontSize: "1rem",
                            fontWeight: "bold",
                            borderRadius: "12px",
                            minWidth: "200px",
                            "&:hover": {
                              background: "linear-gradient(97.89deg, #3311cc, #8866dd)"
                            }
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={24} color="inherit" />
                          ) : (
                            <>
                              <IoCheckmarkCircle size="20px" style={{ marginRight: "8px" }} />
                              Submit Registration
                            </>
                          )}
                        </VuiButton>
                      </VuiBox>
                    </VuiBox>
                  )}

                  {!userType && (
                    <VuiBox
                      textAlign="center"
                      py={5}
                      sx={{
                        background: "rgba(255, 255, 255, 0.02)",
                        borderRadius: "15px",
                        border: "1px solid rgba(255, 255, 255, 0.1)"
                      }}
                    >
                      <VuiTypography variant="h6" color="text" fontWeight="regular">
                        Please select a user type to continue
                      </VuiTypography>
                    </VuiBox>
                  )}
                </form>
              </Card>
            </Grid>
          </Grid>
        </VuiBox>
      </VuiBox>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default UserSubmissionForm;

